<template>
  <div>
    <f-data-table id="premium_feeds_table"
                  data-uri="/api/v1/live-feeds"
                  :columns="columns"
                  :pagination="{
                    enabled: true,
                    per_page: 10,
                    default_page: 1,
                  }"
                  heading="Live Feeds"
                  :auto-hide-columns="true"
                  :locked="true"
                  icon="la la-file-audio-o"
                  :server-side="true">
      <!-- ACTIONS -->
      <template #_actions="props">
        <div class="kt-portlet__head-toolbar">
          <div class="kt-portlet__head-wrapper">
            <button v-perms.disable="['api.live-feeds.store']" type="button"
                    class="btn btn-ss-standout btn-icon-sm"
                    @click="windowOpen('/live-feeds/create')">
              <i class="flaticon2-plus" />New Live Feed
            </button>
            <button type="button"
                    class="btn btn-ss btn-icon-sm"
                    @click="props.reloadData">
              <i class="flaticon2-refresh" />Reload Data
            </button>
          </div>
        </div>
      </template>

      <template #assigned_feeds="props">
        <span v-for="(feed, index) in props.row.feeds" :key="index">
          <a :href="`/feeds/${feed.uuid}/edit`" class="kt-link" target="_blank">{{ feed.name }}</a>
        </span>
      </template>

      <template #mounts="props">
        <div style="word-wrap: break-word">
          <span v-for="(output, index) in props.row.premium_config.outputs" :key="index">
            {{ output.mount }} (<span class="text-uppercase">{{ output.format | output_format_filter }}</span> @ {{ output.bitrate }} kHhz)<br>
          </span>
        </div>
      </template>

      <template #status="props">
        <span v-if="checkStatus(props.row)" class="kt-font-success"><i class="fa fa-play" />On Air</span>
        <span v-else class="kt-font-danger"><i class="fa fa-stop" />Off Air</span>
      </template>

      <template #start_at="props">
        <span v-if="props.row.start_at">{{ formatDateTime(props.row.start_at) }}</span>
      </template>
      <template #stop_at="props">
        <span v-if="props.row.stop_at">{{ formatDateTime(props.row.stop_at) }}</span>
      </template>
      <template #restart_at="props">
        <span v-if="props.row.restart_at">{{ formatDateTime(props.row.restart_at) }}</span>
      </template>

      <template #actions="props">
        <b-dropdown split
                    text="Details"
                    :split-href="`/live-feeds/${props.row.uuid}`">
          <li role="presentation">
            <button v-perms.disable="['api.live-feeds.show']" role="menuitem"
                    type="button"
                    class="dropdown-item" @click="windowOpen(`/live-feeds/${props.row.uuid}/edit`)">
              <i class="fa fa-edit" />Edit
            </button>
          </li>

          <b-dropdown-divider />

          <li v-if="!checkStatus(props.row)" role="presentation">
            <button v-perms.disable="['api.live-feeds.control']" role="menuitem"
                    type="button"
                    class="dropdown-item" @click="props.do({cb: () => control('start', props.row)})">
              <i class="fa fa-play" />Start
            </button>
          </li>

          <li v-else role="presentation">
            <button v-perms.disable="['api.live-feeds.control']" role="menuitem"
                    type="button"
                    class="dropdown-item" @click="props.do({cb: () => control('stop', props.row)})">
              <i class="fa fa-stop" />Stop
            </button>
          </li>

          <li v-if="checkStatus(props.row)" role="presentation">
            <button v-perms.disable="['api.live-feeds.control']" role="menuitem"
                    type="button"
                    class="dropdown-item" @click="props.do({cb: () => control('restart', props.row)})">
              <i class="fa fa-refresh" />Restart
            </button>
          </li>

          <b-dropdown-divider />

          <li role="presentation">
            <button v-perms.disable="['api.live-feeds.destroy']" role="menuitem"
                    type="button"
                    class="dropdown-item" @click="props.do({cb: () => destroy(props.row)})">
              <i class="fa fa-trash" />Delete
            </button>
          </li>
        </b-dropdown>
      </template>
    </f-data-table>
    <confirm-modal v-if="confirm_modal_message"
                   name="confirm-action-modal"
                   title="Are you sure?"
                   icon="la la-exclamation-triangle">
      <div v-sanitise="confirm_modal_message" />
    </confirm-modal>
    <quick-alert-modal v-bind="quick_alert" />
  </div>
</template>

<script>

import {
  BDropdown,
  BDropdownDivider,
} from 'bootstrap-vue';

import ConfirmModal from "VueModals/ConfirmModal.vue";
import FDataTable from "VuePartials/App/Storage/FDataTable/FDataTable.vue";
import LiveFeedsMixin from "VueMixins/Feeds/LiveFeedsMixin";

export default {

  components: {
    ConfirmModal,
    FDataTable,
    BDropdown,
    BDropdownDivider,
  },

  mixins: [LiveFeedsMixin],

  data: () => ({
    columns: [
      {
        field: 'name',
        display: 'Name',
        sort_dir: 'asc',
        never_clip: true,
      },
      {
        field: 'assigned_feeds',
        display: 'Assigned Feeds',
        sortable: false,
        clip_resistance: 0,
        toggleable: true,
      },
      {
        field: 'start_at',
        display: 'Last Start',
        sort_dir: 'asc',
        clip_resistance: 1,
        toggleable: true,
      },
      {
        field: 'stop_at',
        display: 'Last Stop',
        sort_dir: 'asc',
        clip_resistance: 1,
        toggleable: true,
      },
      {
        field: 'restart_at',
        display: 'Last Restart',
        sort_dir: 'asc',
        clip_resistance: 1,
        toggleable: true,
      },
      {
        field: 'status',
        display: 'Status',
        sort_dir: 'asc',
        never_clip: true,
        toggleable: true,
      },
      {
        field: 'mounts',
        display: 'Mounts',
        sortable: false,
        clip_resistance: 2,
      },
      {
        field: `actions`,
        display: `Actions`,
        sortable: false,
        width: 200,
        min_width: 140,
        never_clip: true,
      },
    ],
    premium: null,
  }),

  methods: {
    /**
     * Redirect to show route
     */
    show(uuid) {
      window.open(`/live-feeds/${uuid}`, '_blank');
    },

    /**
     * Redirect to timbre player
     */
    previewMount(output) {
      window.open(`${window.timbre_player}/live/${output.mount}`, '_blank');
    },
  },
};
</script>
